import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { generatePath } from 'react-router-dom'

import {
  LotteryUserInfoQuery,
  LotteryUserInfoQueryVariables,
  SendVerificationSms,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  UpdatePersonalInformationSettings,
  UpdatePersonalInformationSettingsMutation,
  UpdatePersonalInformationSettingsMutationVariables,
  UpdateUserDeliveryInfo,
  UpdateUserDeliveryInfoMutation,
  UpdateUserDeliveryInfoMutationVariables,
  UserDeliveryDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { needToRegisterUserAddress } from '@/components/pages/LotteryDetail/viewModel'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getPrefrenceObject } from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages } =
    useRouter<{ preSaleId: string; clientId?: string; token?: string }>()
  const { showError } = useMessage()
  const [openSMSVerificationModal, setOpenSMSVerificationModal] =
    useState<boolean>(false)
  const { t } = useTranslation('')

  const { data } = useQuery<
    LotteryUserInfoQuery,
    LotteryUserInfoQueryVariables
  >(QUERY, {
    variables: { id: router.query.preSaleId },
  })

  const [updateUserDeliveryInfo] = useMutation<
    UpdateUserDeliveryInfoMutation,
    UpdateUserDeliveryInfoMutationVariables
  >(UpdateUserDeliveryInfo)

  const [updateUserInfo] = useMutation<
    UpdatePersonalInformationSettingsMutation,
    UpdatePersonalInformationSettingsMutationVariables
  >(UpdatePersonalInformationSettings)

  const [sendVerificationSMS, { loading: sendingVerificationLoading }] =
    useMutation<
      SendVerificationSmsMutation,
      SendVerificationSmsMutationVariables
    >(SendVerificationSms)

  return {
    state: {
      user: data?.user ?? null,
      openSMSVerificationModal,
      sendingVerificationLoading,
    },
    actions: {
      async onSubmit(value) {
        const [updatedUserDeliveryInfo] = await Promise.all([
          updateUserDeliveryInfo({
            variables: {
              updateUserDeliveryInfoInputs: {
                fullName: value.fullName,
                phoneNumber: value.phoneNumber,
                address: {
                  postalCode: data?.user?.address?.postalCode ?? '',
                  prefecture: data?.user?.address?.prefecture ?? '',
                  city: data?.user?.address?.city ?? '',
                  line1: data?.user?.address?.line1 ?? '',
                  line2: data?.user?.address?.line2 ?? '',
                },
              },
            },
          }),
          updateUserInfo({
            variables: {
              inputs: {
                birthday: value.birthday,
                gender: value.gender,
              },
            },
          }),
        ])

        if (
          !updatedUserDeliveryInfo.data?.updateUserDeliveryInfo
            .isPhoneNumberVerified
        ) {
          return setOpenSMSVerificationModal(true)
        }

        const values = await getPrefrenceObject<{
          isFirstComeFirstServed?: boolean
        }>('preSaleLotteryApplicationInput')

        const personalInformationRequirements = values?.isFirstComeFirstServed
          ? data?.preSale?.firstComeFirstServed?.personalInformationRequirements
          : data?.preSale.lottery?.personalInformationRequirements

        if (
          needToRegisterUserAddress(
            personalInformationRequirements ?? [],
            data?.user
          )
        ) {
          return router.replace(
            generatePath(routerPages.LotteryDeliveryInfo, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        if (values?.isFirstComeFirstServed) {
          return router.replace(
            generatePath(routerPages.FirstComeFirstServedConfirmation, {
              preSaleId: router.query.preSaleId,
            })
          )
        }

        router.replace(
          generatePath(routerPages.LotteryConfirmation, {
            preSaleId: router.query.preSaleId,
          })
        )
      },
      onCloseSMSVerificationModal() {
        setOpenSMSVerificationModal(false)
      },
      async onSendVerificationSMS() {
        await sendVerificationSMS()
        router.replace(routerPages.SMSVerification)
      },
    },
  }
})

const QUERY = gql`
  query LotteryUserInfo($id: ID!, $clientId: String, $token: String) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      id
      lottery {
        personalInformationRequirements
      }
      firstComeFirstServed {
        personalInformationRequirements
      }
    }
    user {
      ...UserDeliveryDetail
    }
  }
  ${UserDeliveryDetailFragmentDoc}
`
